import { FormNumberInput } from "@/components/atom/numberInput/form/FormNumberInput";
import { FormTextInput } from "@/components/atom/textInput/form/FormTextInput";
import { FormDescription } from "@/components/atom/title/form/FormDescription";
import { FormSubTitle } from "@/components/atom/title/form/FormSubTitle";
import { MantineSelectableTable } from "@/components/molecule/mantineSelectableTable/MantineSelectableTable";
import { MultiBarcodeInput } from "@/components/molecule/multiBarcodeInput/MultiBarcodeInput";
import { InventoriesAutoComplete } from "@/components/organism/autoComplete/inventory/inventories-autoComplete";
import { LocationAutoComplete } from "@/components/organism/autoComplete/location/location-autoComplate";
import FormTemplate from "@/components/template/form/FormTemplate";
import { COMMON_TEXT } from "@/constants/text";
import { useModal } from "@/context/ModalStackManager";
import { useInventoriesFormFetch } from "@/fetch/form/useInventoriesFormFetch";
import { useRawMaterialAllHandler } from "@/hooks/handler/useRawMaterialAllHandler";
import { useWorkDataStore } from "@/store/work.store";
import { customNotification } from "@/utils/notificationShow";
import { Flex, Pagination, Text } from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner,
  WorksBulkInputPutRequestWorkLotPairsInner,
} from "@sizlcorp/sizl-api-document/dist/models";
import { MRT_ColumnDef, MRT_Row } from "mantine-react-table";
import { useMemo } from "react";

const RawMaterialSelectLocationAll = Object.assign(
  {},
  {
    Template: FormTemplate,
    SubTitle: FormSubTitle,
    Description: FormDescription,
    TextInput: FormTextInput,
    Text: Text,
    NumberInput: FormNumberInput,
    BarcodeInput: MultiBarcodeInput,
    Table: MantineSelectableTable,
    InventoriesAutoComplete: InventoriesAutoComplete,
    LocationAutoComplete: LocationAutoComplete,
    Pagination: Pagination,
  }
);

export const RawMaterialSelectLocationAllForm = () => {
  const { closeModal } = useModal();
  const { workData } = useWorkDataStore((state) => ({
    workData: state.workData,
  }));

  const { work } = workData;
  const { onSubmit } = useRawMaterialAllHandler();
  const fromLocationCode = work?.locationSetting?.fromLocation?.code;
  const targetLocationName = work?.locationSetting?.toLocation?.name;

  const form = useForm({
    initialValues: {
      itemCode: "",
      sourceLocationCode: fromLocationCode || "",
      useBarcode: false,
      barcodeInput: "",
      worksBulkInputPutRequest: [],
    },
  });

  const targetLocationCode = work?.locationSetting?.toLocation?.code;

  const columnsBoms = useMemo<MRT_ColumnDef[]>(
    () => [
      {
        accessorKey: "itemCode",
        header: COMMON_TEXT.COLUMN.ITEM_CODE,
      },
      {
        accessorKey: "itemName",
        header: COMMON_TEXT.COLUMN.ITEM_NAME,
      },
      {
        accessorKey: "routingCode",
        header: COMMON_TEXT.COLUMN.ROUTING_CODE,
      },
    ],
    []
  );
  const columns = useMemo<MRT_ColumnDef[]>(
    () => [
      {
        accessorKey: "order",
        header: COMMON_TEXT.COLUMN.ORDER,
      },
      {
        accessorKey: "itemCode",
        header: COMMON_TEXT.COLUMN.ITEM_CODE,
      },
      {
        accessorKey: "itemName",
        header: COMMON_TEXT.COLUMN.ITEM_NAME,
      },
      {
        accessorKey: "lotName",
        header: COMMON_TEXT.COLUMN.LOT_NAME,
      },
      {
        accessorKey: "lotExpiration",
        header: COMMON_TEXT.COLUMN.LOT_EXPIRATION_DATE,
      },
      {
        accessorKey: "locationCode",
        header: COMMON_TEXT.COLUMN.LOT_LOCATION,
      },
      {
        accessorKey: "quantity",
        header: COMMON_TEXT.COLUMN.QUANTITY,
        Cell: ({ cell }) => {
          return (
            <FormNumberInput
              w={"6rem"}
              {...form.getInputProps(
                `worksBulkInputPutRequest.${cell.row.index}.quantity`
              )}
              value={Number(cell.getValue())}
            />
          );
        },
      },
    ],
    []
  );

  const { data, refetch } = useInventoriesFormFetch({
    itemCodes: form.values.itemCode ?? undefined,
    locationCode: form.values.sourceLocationCode,
    lotName: form.values.barcodeInput,
    // enabled: customFunctions.ADD_STANDARD_INFO_BOM_SEARCH_MENU_SETTING || Boolean(form.values.useBarcode && form.values.barcodeInput),
  });

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (!form.values.barcodeInput) return;
      if (checkDuplicateLot(form.values.barcodeInput)) return;
      await form.setFieldValue("barcodeInput", form.values.barcodeInput);
      await form.setFieldValue("useBarcode", true);
      await refetchHandler();
    }
  };

  const handleBlur = async (event: React.FocusEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (!inputValue) return;
    if (checkDuplicateLot(inputValue)) return;
    await form.setFieldValue("barcodeInput", inputValue);
    await form.setFieldValue("useBarcode", true);
    await refetchHandler();
  };

  const handleRowDelete = (row: MRT_Row<any>) => {
    form.removeListItem("worksBulkInputPutRequest", row.index);
  };

  const checkDuplicateLot = (value: string) => {
    if (
      form.values.worksBulkInputPutRequest.some(
        (data: any) => data.lotId === Number(value)
      )
    ) {
      customNotification.error({
        message: COMMON_TEXT.NOTIFICATION.DUPLICATE_LOT,
      });
      form.setFieldValue("barcodeInput", "");
      return true;
    }
    return false;
  };

  const handleOnSubmit = () => {
    const request = form.values.worksBulkInputPutRequest.map(
      (data: WorksBulkInputPutRequestWorkLotPairsInner) => ({
        workId: work?.id as number,
        lotId: data.lotId,
        sourceLocationCode: data.sourceLocationCode,
        targetLocationCode: data.targetLocationCode,
        quantity: String(data.quantity),
      })
    );

    onSubmit({ workLotPairs: request });
  };

  const handleInventories = async (value: string | null) => {
    if (value === null) return;
    if (!checkDuplicateLot(value)) {
      const { lot } = data?.data?.filter(
        (data) => data.lotId === Number(value)
      )[0] as ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner;

      const lotName = lot?.name ?? "";

      await form.setFieldValue("barcodeInput", lotName);
      await form.setFieldValue("useBarcode", true);
      await refetchHandler();
    }
  };

  const routingBoms = work?.routingData?.routingBoms;
  const convertedRoutingBoms = routingBoms?.map((bom) => ({
    itemName: bom.item?.name,
    itemCode: bom.itemCode,
    routingCode: bom.routingCode,
  }));

  const refetchHandler = async () => {
    const data = await refetch();
    const inventoryData = data?.data?.data;
    const noData = !inventoryData?.length;
    if (noData) {
      form.setFieldValue("barcodeInput", "");
      customNotification.error({
        message: COMMON_TEXT.NOTIFICATION.LOT_NOT_FOUND,
      });
    } else {
      const { lotId, itemCode, itemName, lot, locationCode, quantity } =
        inventoryData[0] as ItemsGet200ResponseRowsInnerLotsInnerInventoriesInner;
      form.setFieldValue("barcodeInput", "");
      form.insertListItem("worksBulkInputPutRequest", {
        lotName: lot?.name,
        lotExpiration: lot?.expiration,
        lotId,
        itemCode,
        itemName,
        sourceLocationCode: locationCode,
        targetLocationCode: targetLocationCode,
        quantity,
      });
    }
  };

  const handleOnRowSelection = (selectedRows: any) => {
    form.setFieldValue("itemCode", selectedRows[0]?.itemCode);
  };

  return (
    <RawMaterialSelectLocationAll.Template
      w={"90em"}
      onSubmit={handleOnSubmit}
      onCancel={() => closeModal({})}
      label="일괄 투입"
    >
      <RawMaterialSelectLocationAll.SubTitle>
        {COMMON_TEXT.FORM.RAW_MATERIAL_ALL_SUBTITLE}
      </RawMaterialSelectLocationAll.SubTitle>
      <RawMaterialSelectLocationAll.Table
        enableMultiRowSelection={false}
        columns={columnsBoms}
        data={convertedRoutingBoms ?? []}
        onRowSelection={handleOnRowSelection}
      />
      <Flex w={"100%"} gap={"xl"} justify={"space-between"}>
        <RawMaterialSelectLocationAll.LocationAutoComplete
          w={"100%"}
          withAsterisk
          label={COMMON_TEXT.LABEL.LOCATION}
          {...form.getInputProps("sourceLocationCode")}
        />
      </Flex>
      <Flex w={"100%"} gap={"xl"} justify={"space-between"}>
        <RawMaterialSelectLocationAll.InventoriesAutoComplete
          w={"100%"}
          disabled={!form.values.sourceLocationCode}
          label={COMMON_TEXT.LABEL.LOT_INFORM}
          value={form.values.barcodeInput}
          onChange={handleInventories}
          query={
            form.values.itemCode
              ? {
                  itemCode: form.values.itemCode,
                  locationCode: form.values.sourceLocationCode,
                  quantity: { $gt: 0 },
                }
              : {
                  itemCode: undefined,
                  locationCode: form.values.sourceLocationCode,
                  quantity: { $gt: 0 },
                }
          }
        />
      </Flex>
      <Flex justify={"space-between"}>
        <RawMaterialSelectLocationAll.BarcodeInput
          disabled={!form.values.sourceLocationCode}
          handleBlur={handleBlur}
          onKeyDown={handleKeyDown}
          {...form.getInputProps("barcodeInput")}
          label={COMMON_TEXT.LABEL.INPUT_BARCODE_INFORM}
          required
        />
        <Flex gap={"xl"} align={"center"}>
          <RawMaterialSelectLocationAll.TextInput
            withAsterisk
            disabled
            label={COMMON_TEXT.LABEL.TARGET_LOCATION_CODE}
            value={targetLocationName}
          />
        </Flex>
      </Flex>
      <RawMaterialSelectLocationAll.Table
        enableRowActions={true}
        enableRowSelection={false}
        localization={{
          noRecordsToDisplay: COMMON_TEXT.DATA_EMPTY.NO_LOT_INFORM,
        }}
        columns={columns}
        data={form.values.worksBulkInputPutRequest.map(
          (data: WorksBulkInputPutRequestWorkLotPairsInner, index: number) => ({
            order: index + 1,
            ...data,
          })
        )}
        onClickDelete={handleRowDelete}
      />
    </RawMaterialSelectLocationAll.Template>
  );
};
